import { WORKSPACE_KPI_REGISTRY } from './workspace-kpis';
import type { AnalysisSpec } from '@lognai/plugin-analytics-ui';

export type WorkspaceAiKind = 'chart' | 'kpi' | 'card';
export const customKpiId = (visualId: string) => `kpi:visual:${visualId}`;
export const customKpiVisualId = (id: string) => id.startsWith('kpi:visual:') ? id.slice(11) : undefined;

export function workspaceAiStarters(data: { sales: boolean; inventory: boolean; purchaseOrders: boolean; shipments: boolean }, kind: WorkspaceAiKind): string[] {
  const queries = [
    { available: data.sales, chart: 'Show monthly sales revenue', kpi: 'Total sales revenue', card: 'Customers with the highest sales revenue' },
    { available: data.inventory, chart: 'Show inventory value by product', kpi: 'How many products are out of stock?', card: 'Products below their reorder point' },
    { available: data.purchaseOrders, chart: 'Overdue purchase order value by supplier', kpi: 'How many purchase orders are open?', card: 'Suppliers with the highest overdue purchase order value' },
    { available: data.shipments, chart: 'Expected shipment arrivals by day', kpi: 'How many shipments are on hold?', card: 'Shipments with the highest ETA risk' },
  ];
  return queries.filter((item) => item.available).map((item) => item[kind]);
}

/** Map generated plans to the same source permissions used by workspace widgets. */
export function workspaceAiSource(view: string) {
  const registered = WORKSPACE_KPI_REGISTRY.find((item) => item.measure?.startsWith(`${view}.`));
  if (registered) return registered;
  const key = /supplier|purchase_order/.test(view) ? 'purchase-orders' :
    /^(sales|order_|customer_|supply_)/.test(view) ? 'orders' :
    /^inventory/.test(view) ? 'inventory' : /^warehouse/.test(view) ? 'warehouse' :
    /^invoice/.test(view) ? 'invoices' : /^shipment/.test(view) ? 'shipments' : undefined;
  if (key === 'purchase-orders') return { plugin: 'orders', permission: 'order:purchase:read', module: 'Orders', href: '/orders/purchase' };
  return WORKSPACE_KPI_REGISTRY.find((item) => item.plugin === key && !item.measure);
}

export function workspaceKpiAnalysis(analysis: AnalysisSpec): AnalysisSpec {
  if (analysis.measures.length !== 1) throw new Error('Describe a single metric to create a KPI.');
  const range = analysis.timeDimension?.dateRange;
  return {
    ...analysis, dimensions: [], timeDimension: undefined, drillPath: [], order: [], template: 'horizontal_bar',
    filters: range ? [...analysis.filters, { member: analysis.timeDimension!.member, operator: 'inDateRange', values: range }] : analysis.filters,
  };
}
